/**
 * Get Element Schema Tool
 * 提供元素类型的属性定义，帮助 AI 了解每个元素支持的属性和默认值
 */

import { BlockManager } from "../../src/utils/BlockManager";
import { StandardType, BasicType } from "../../src/constants/blockType";

// 支持的元素类型列表
const SUPPORTED_ELEMENT_TYPES = {
  // Page
  page: BasicType.PAGE,

  // Standard elements
  "standard-section": StandardType.STANDARD_SECTION,
  "standard-column": StandardType.STANDARD_COLUMN,
  "standard-text": StandardType.STANDARD_TEXT,
  "standard-paragraph": StandardType.STANDARD_PARAGRAPH,
  "standard-h1": StandardType.STANDARD_H1,
  "standard-h2": StandardType.STANDARD_H2,
  "standard-h3": StandardType.STANDARD_H3,
  "standard-h4": StandardType.STANDARD_H4,
  "standard-image": StandardType.STANDARD_IMAGE,
  "standard-button": StandardType.STANDARD_BUTTON,
  "standard-divider": StandardType.STANDARD_DIVIDER,
  "standard-spacer": StandardType.STANDARD_SPACER,
  "standard-navbar": StandardType.STANDARD_NAVBAR,
  "standard-navbar-link": StandardType.STANDARD_NAVBAR_LINK,
  "standard-social": StandardType.STANDARD_SOCIAL,
  "standard-social-element": StandardType.STANDARD_SOCIAL_ELEMENT,
  "standard-hero": StandardType.STANDARD_HERO,
} as const;

// 属性分类定义
const ATTRIBUTE_CATEGORIES: Record<string, string[]> = {
  layout: [
    "width",
    "height",
    "padding",
    "padding-top",
    "padding-bottom",
    "padding-left",
    "padding-right",
    "margin",
    "margin-top",
    "margin-bottom",
    "inner-padding",
    "inner-padding-top",
    "inner-padding-bottom",
    "inner-padding-left",
    "inner-padding-right",
    "align",
    "vertical-align",
  ],
  background: [
    "background-color",
    "background-url",
    "background-repeat",
    "background-size",
    "background-position",
    "background-position-x",
    "background-position-y",
    "background-image-enabled",
  ],
  border: [
    "border",
    "border-radius",
    "border-width",
    "border-style",
    "border-color",
    "border-enabled",
  ],
  typography: [
    "color",
    "font-family",
    "font-size",
    "font-weight",
    "font-style",
    "line-height",
    "letter-spacing",
    "text-align",
    "text-decoration",
    "text-transform",
  ],
  link: ["href", "target", "rel"],
  image: ["src", "alt", "title", "fluid-on-mobile"],
  icon: [
    "icon-enabled",
    "icon-src",
    "icon-width",
    "icon-height",
    "icon-position",
    "icon-vertical-align",
  ],
  other: ["css-class", "mj-class", "direction", "mode", "spacing"],
};

export interface GetElementSchemaInput {
  elementType?: string;
}

export interface CategorizedAttributes {
  layout: Record<string, any>;
  background: Record<string, any>;
  border: Record<string, any>;
  typography: Record<string, any>;
  link: Record<string, any>;
  image: Record<string, any>;
  icon: Record<string, any>;
  other: Record<string, any>;
}

export interface DetailedElementSchema {
  type: string;
  name: string;
  description: string;
  /** 按类别分组的默认属性 */
  attributesByCategory: CategorizedAttributes;
  /** 所有默认属性（平铺） */
  allDefaultAttributes: Record<string, any>;
  /** 默认 data */
  defaultData: Record<string, any>;
  /** 默认 children */
  defaultChildren: any[];
  /** children 说明 */
  childrenDescription: string;
  /** 使用示例 */
  example: any;
}

export interface GetElementSchemaOutput {
  success: boolean;
  schema?: DetailedElementSchema;
  allTypes?: Array<{ type: string; name: string; description: string }>;
  error?: string;
}

/**
 * 将属性按类别分组
 */
function categorizeAttributes(
  attributes: Record<string, any>
): CategorizedAttributes {
  const result: CategorizedAttributes = {
    layout: {},
    background: {},
    border: {},
    typography: {},
    link: {},
    image: {},
    icon: {},
    other: {},
  };

  for (const [key, value] of Object.entries(attributes)) {
    let found = false;
    for (const [category, keys] of Object.entries(ATTRIBUTE_CATEGORIES)) {
      if (keys.includes(key)) {
        (result as any)[category][key] = value;
        found = true;
        break;
      }
    }
    if (!found) {
      result.other[key] = value;
    }
  }

  return result;
}

/**
 * 生成元素使用示例
 */
function generateExample(
  elementType: string,
  defaultAttrs: Record<string, any>
): any {
  const baseExample: any = {
    type: elementType,
    data: {},
    attributes: {},
    children: [],
  };

  // 根据类型生成不同的示例
  switch (elementType) {
    case "standard-paragraph":
    case "standard-text":
    case "standard-h1":
    case "standard-h2":
    case "standard-h3":
    case "standard-h4":
      return {
        ...baseExample,
        attributes: {
          align: "center",
          color: "#333333",
        },
        children: [{ text: "示例文本" }],
      };

    case "standard-button":
      return {
        ...baseExample,
        attributes: {
          "background-color": defaultAttrs["background-color"] || "#414141",
          color: defaultAttrs["color"] || "#ffffff",
          align: "center",
          href: "https://example.com",
        },
        children: [{ text: "点击按钮" }],
      };

    case "standard-image":
      return {
        ...baseExample,
        attributes: {
          src: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97",
          alt: "A modern workspace with laptop and coffee on wooden desk, natural lighting",
          width: "600px", // 只支持 px 单位或留空（全宽），不支持百分比
          align: "center",
          "padding-top": "0px",
          "padding-bottom": "0px",
          "padding-left": "0px",
          "padding-right": "0px",
        },
        children: [{ text: "" }],
      };

    case "standard-divider":
      return {
        ...baseExample,
        attributes: {
          "border-color": "#CCCCCC",
          "border-style": "solid",
          "border-width": "1px",
          width: "100%",
        },
        children: [{ text: "" }],
      };

    case "standard-spacer":
      return {
        ...baseExample,
        attributes: {
          height: "20px",
        },
        children: [{ text: "" }],
      };

    case "standard-section":
      return {
        ...baseExample,
        attributes: {
          "background-color": "#FFFFFF",
          "padding-top": "20px", // padding 必须显式写出
          "padding-bottom": "20px",
          "padding-left": "0px",
          "padding-right": "0px",
        },
        children: [
          {
            type: "standard-column",
            data: {},
            attributes: { width: "100%" },
            children: [],
          },
        ],
      };

    case "standard-column":
      return {
        ...baseExample,
        attributes: {
          width: "100%",
          "vertical-align": "top",
        },
        children: [],
      };

    case "standard-navbar":
      return {
        ...baseExample,
        attributes: {
          align: "center",
        },
        children: [
          {
            type: "standard-navbar-link",
            data: {},
            attributes: { href: "https://example.com" },
            children: [{ text: "链接" }],
          },
        ],
      };

    case "standard-social":
      return {
        ...baseExample,
        attributes: {
          align: "center",
          "icon-size": "24px",
        },
        children: [
          {
            type: "standard-social-element",
            data: {},
            attributes: {
              src: "https://example.com/icon.png",
              href: "https://facebook.com",
            },
            children: [{ text: "" }],
          },
        ],
      };

    default:
      return baseExample;
  }
}

/**
 * 获取 children 的说明
 */
function getChildrenDescription(elementType: string): string {
  const descriptions: Record<string, string> = {
    page: "Section 元素数组",
    "standard-section": "Column 元素数组",
    "standard-column": "内容元素数组（paragraph, image, button 等）",
    "standard-text":
      "TextNode 数组：[{ text: '文本', bold?: true, link?: { href: '...' } }]",
    "standard-paragraph": "TextNode 数组：[{ text: '文本' }]",
    "standard-h1": "TextNode 数组：[{ text: '标题' }]",
    "standard-h2": "TextNode 数组：[{ text: '标题' }]",
    "standard-h3": "TextNode 数组：[{ text: '标题' }]",
    "standard-h4": "TextNode 数组：[{ text: '标题' }]",
    "standard-button": "TextNode 数组：[{ text: '按钮文字' }]",
    "standard-image": "空 TextNode：[{ text: '' }]",
    "standard-divider": "空 TextNode：[{ text: '' }]",
    "standard-spacer": "空 TextNode：[{ text: '' }]",
    "standard-navbar": "NavbarLink 元素数组",
    "standard-navbar-link": "TextNode 数组：[{ text: '链接文字' }]",
    "standard-social": "SocialElement 元素数组",
    "standard-social-element": "空 TextNode：[{ text: '' }]",
    "standard-hero": "内容元素数组（paragraph, button 等）",
  };
  return descriptions[elementType] || "子元素数组";
}

/**
 * 获取元素类型的描述
 */
function getElementDescription(elementType: string): string {
  const descriptions: Record<string, string> = {
    page: "邮件模板的根元素，包含全局配置和所有 sections",
    "standard-section": "区块容器，包含一行或多列（columns）",
    "standard-column": "列容器，通过 width 属性控制宽度比例（如 '50%'）",
    "standard-text": "通用文本元素",
    "standard-paragraph": "段落文本，默认 16px",
    "standard-h1": "一级标题，默认 40px 加粗",
    "standard-h2": "二级标题，默认 32px 加粗",
    "standard-h3": "三级标题，默认 24px 加粗",
    "standard-h4": "四级标题，默认 18px 加粗",
    "standard-image": "图片元素，src 为必填属性",
    "standard-button": "按钮元素，href 设置链接",
    "standard-divider": "水平分割线",
    "standard-spacer": "垂直间距，height 控制高度",
    "standard-navbar": "导航栏容器",
    "standard-navbar-link": "导航链接项",
    "standard-social": "社交图标容器",
    "standard-social-element": "社交图标项，src 设置图标，href 设置链接",
    "standard-hero": "主视觉区域，支持背景图",
  };
  return descriptions[elementType] || "";
}

/**
 * 获取元素的 schema 信息
 */
export function getElementSchema(
  input: GetElementSchemaInput
): GetElementSchemaOutput {
  try {
    const { elementType } = input;

    // 如果没有指定类型，返回所有支持的类型列表（简化版）
    if (!elementType) {
      const allTypes = Object.keys(SUPPORTED_ELEMENT_TYPES).map((type) => ({
        type,
        name: type,
        description: getElementDescription(type),
      }));

      return {
        success: true,
        allTypes,
      };
    }

    // 检查是否是支持的类型
    if (!(elementType in SUPPORTED_ELEMENT_TYPES)) {
      return {
        success: false,
        error: `不支持的元素类型: ${elementType}`,
        allTypes: Object.keys(SUPPORTED_ELEMENT_TYPES).map((type) => ({
          type,
          name: type,
          description: getElementDescription(type),
        })),
      };
    }

    const typeValue =
      SUPPORTED_ELEMENT_TYPES[
        elementType as keyof typeof SUPPORTED_ELEMENT_TYPES
      ];
    const blockDef = BlockManager.getBlockByType(typeValue as any);

    if (!blockDef) {
      return {
        success: false,
        error: `未找到元素定义: ${elementType}`,
      };
    }

    // 获取完整的默认值：
    // 1. blockDef.defaultData.attributes 包含所有预设的默认属性
    // 2. blockDef.create({}) 返回的元素可能有额外的默认 data/children
    // 注意：create({}) 返回的 attributes 通常是空的，因为默认值在渲染时从 defaultData 合并
    let createdElement: any = null;
    const defaultDataAttributes = blockDef.defaultData?.attributes || {};

    if (blockDef.create) {
      try {
        createdElement = blockDef.create({});
      } catch {
        // 如果 create 失败，继续使用 defaultData
      }
    }

    // defaultData.attributes 包含了完整的默认属性值
    const allDefaultAttributes = defaultDataAttributes;

    return {
      success: true,
      schema: {
        type: elementType,
        name: blockDef.name || elementType,
        description: getElementDescription(elementType),
        attributesByCategory: categorizeAttributes(allDefaultAttributes),
        allDefaultAttributes: allDefaultAttributes,
        defaultData: createdElement?.data || blockDef.defaultData?.data || {},
        defaultChildren:
          createdElement?.children || blockDef.defaultData?.children || [],
        childrenDescription: getChildrenDescription(elementType),
        example: generateExample(elementType, allDefaultAttributes),
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export const GET_ELEMENT_SCHEMA_TOOL = {
  name: "get_element_schema",
  description: `获取邮件元素的完整属性定义和默认值。

⚠️ 通过 BlockDefinition.defaultData.attributes 获取完整的默认属性！

使用方式:
- 不传参数: 返回所有支持的元素类型列表
- 传 elementType: 返回指定元素的详细属性定义

返回内容:
- attributesByCategory: 按类别分组的默认属性（layout/background/border/typography 等）
- allDefaultAttributes: 所有默认属性（从 defaultData.attributes 获取，最完整）
- defaultData: 默认 data 对象
- defaultChildren: 默认 children
- childrenDescription: children 格式说明
- example: 使用示例

示例:
- get_element_schema({}) → 返回所有类型列表
- get_element_schema({ elementType: "standard-button" }) → 返回按钮的完整属性和示例`,
  inputSchema: {
    type: "object" as const,
    properties: {
      elementType: {
        type: "string",
        description:
          "要查询的元素类型，如 'standard-button'、'standard-image' 等",
      },
    },
    required: [],
  },
};
